import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
} from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  DashboardChartSeries,
  DashboardRevenueCardProps,
} from "@/modules/dashboard/types/dashboard-analytics.types";

/**
 * Plot geometry, in a unitless 0..100 × 0..100 space that `preserveAspectRatio
 * ="none"` stretches to the card width. Nothing inside the SVG may be a glyph
 * or a circle: non-uniform scaling would distort both. Text, markers, the
 * crosshair and the tooltip are therefore HTML positioned in percentages —
 * which also keeps Arabic numerals shaping correctly.
 */
const PLOT_TOP = 6;
const PLOT_BOTTOM = 94;
/** Four gridlines including the baseline, as fractions of the y-domain. */
const GRID_LEVELS = [1, 2 / 3, 1 / 3, 0];
/** Evenly-spaced x-axis ticks sampled out of the dense bucket list. */
const X_AXIS_TICK_COUNT = 4;

/** Where a bucket sits along the inline axis, 0..1 from the *inline start*. */
function inlineFraction(index: number, lastIndex: number): number {
  return lastIndex === 0 ? 0 : index / lastIndex;
}

/**
 * Inline fraction → CSS `left` percentage. Under RTL the inline start is the
 * right edge, so time flows leftwards and the earliest bucket sits where an
 * Arabic reader starts reading.
 */
function toLeftPercent(fraction: number, isRtl: boolean): number {
  return (isRtl ? 1 - fraction : fraction) * 100;
}

type ChartGeometry = {
  min: number;
  max: number;
  span: number;
  lastIndex: number;
  /** SVG path `d` per series, in plot space. */
  paths: string[];
  /** Area wash under the first series. */
  areaPath: string;
  /** y (plot space) of each gridline, paired with its domain value. */
  gridlines: { y: number; value: number }[];
  yOf: (value: number) => number;
  xOf: (index: number) => number;
};

/**
 * Every coordinate is COMPUTED from the data — there is no hand-authored path
 * string anywhere. The y-domain spans min…max and always includes 0, so a
 * negative profit bucket (cost above price) plots below the zero line instead
 * of being clamped away.
 */
function buildGeometry(
  series: DashboardChartSeries[],
  isRtl: boolean,
): ChartGeometry {
  const all = series.flatMap((s) => s.values);
  const max = Math.max(...all, 0);
  const min = Math.min(...all, 0);
  // A flat-at-zero range would divide by zero; give it a nominal domain.
  const span = max - min || 1;
  const lastIndex = Math.max(...series.map((s) => s.values.length), 1) - 1;

  const xOf = (index: number): number =>
    toLeftPercent(inlineFraction(index, lastIndex), isRtl);
  const yOf = (value: number): number =>
    PLOT_BOTTOM - ((value - min) / span) * (PLOT_BOTTOM - PLOT_TOP);

  const paths = series.map((s) =>
    s.values.length === 0
      ? ""
      : s.values
          .map(
            (v, i) =>
              `${i === 0 ? "M" : "L"}${xOf(i).toFixed(3)},${yOf(v).toFixed(3)}`,
          )
          .join(" "),
  );

  const first = series[0];
  const areaPath =
    paths[0] && first.values.length > 0
      ? `${paths[0]} L${xOf(first.values.length - 1).toFixed(3)},${yOf(min).toFixed(3)} L${xOf(0).toFixed(3)},${yOf(min).toFixed(3)} Z`
      : "";

  return {
    min,
    max,
    span,
    lastIndex,
    paths,
    areaPath,
    gridlines: GRID_LEVELS.map((level) => {
      const value = min + span * level;
      return { y: yOf(value), value };
    }),
    yOf,
    xOf,
  };
}

/** Indices of the buckets that get an x-axis tick, evenly spaced. */
function tickIndices(lastIndex: number): number[] {
  const count = Math.min(X_AXIS_TICK_COUNT, lastIndex + 1);
  if (count <= 1) return [0];
  return Array.from({ length: count }, (_, i) =>
    Math.round((lastIndex * i) / (count - 1)),
  );
}

/**
 * Sales vs Profit over the selected range.
 *
 * Both series are money in the same currency and share ONE y-axis — never a
 * dual axis, which would invent a correlation that is not in the data.
 *
 * Interaction: a crosshair snaps to the nearest bucket and a single tooltip
 * reads BOTH series at that bucket, so the pointer never has to land on a
 * 2px line. Keyboard users get the same readout via arrow keys. Nothing is
 * gated behind hover — the endpoints are directly labelled and the full
 * numbers live in the `<details>` table view below the plot.
 */
export function DashboardRevenueCard({
  title,
  subtitle,
  series,
  bucketLabels,
  direction,
  orders,
  ordersLabel,
  formatValue,
  emptyLabel,
}: DashboardRevenueCardProps): ReactElement {
  const t = useTranslations();
  const isRtl = direction === "rtl";
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const geometry = useMemo(
    () => buildGeometry(series, isRtl),
    [series, isRtl],
  );
  const { lastIndex } = geometry;

  const hasData = useMemo(
    () => series.some((s) => s.values.some((v) => v !== 0)),
    [series],
  );

  /**
   * Pointer → bucket index. The plot is stretched, so the mapping is a plain
   * ratio of the container width; under RTL the ratio is mirrored because the
   * earliest bucket lives at the right edge.
   */
  const indexFromPointer = useCallback(
    (clientX: number): number => {
      const rect = plotRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return 0;
      const raw = (clientX - rect.left) / rect.width;
      const fraction = isRtl ? 1 - raw : raw;
      return Math.max(0, Math.min(lastIndex, Math.round(fraction * lastIndex)));
    },
    [isRtl, lastIndex],
  );

  const handlePointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      setActiveIndex(indexFromPointer(event.clientX));
    },
    [indexFromPointer],
  );

  const handleKey = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      // Arrow keys walk the buckets in *visual* order, so the key that points
      // at the next bucket on screen is the key that selects it in both
      // directions.
      const forward = isRtl ? "ArrowLeft" : "ArrowRight";
      const backward = isRtl ? "ArrowRight" : "ArrowLeft";
      if (event.key !== forward && event.key !== backward) return;
      event.preventDefault();
      setActiveIndex((current) => {
        const base = current ?? 0;
        const next = event.key === forward ? base + 1 : base - 1;
        return Math.max(0, Math.min(lastIndex, next));
      });
    },
    [isRtl, lastIndex],
  );

  const ticks = tickIndices(lastIndex);
  const activeLabel =
    activeIndex === null ? null : (bucketLabels[activeIndex] ?? "");

  return (
    <section className="rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)] p-4 sm:p-[18px]">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold text-[color:var(--text-primary)]">
            {title}
          </h2>
          <p className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
            {subtitle}
          </p>
        </div>
        {/* Legend — always present for two series, so identity never depends
            on colour matching alone. Line keys mirror the line marks. */}
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {series.map((s) => (
            <li
              key={s.label}
              className="inline-flex items-center gap-2 whitespace-nowrap text-[12px] font-medium text-[color:var(--text-secondary)]"
            >
              <span
                aria-hidden="true"
                className="h-[3px] w-[14px] shrink-0 rounded-full"
                style={{ background: s.color }}
              />
              {s.label}
            </li>
          ))}
        </ul>
      </div>

      {!hasData ? (
        <p className="py-12 text-center text-[13px] text-[color:var(--text-muted)]">
          {emptyLabel}
        </p>
      ) : (
        <div className="mt-4 flex gap-2">
          {/* Y axis — one shared scale for both series. */}
          <div
            className="flex h-[180px] w-11 shrink-0 flex-col justify-between py-[5px] text-end font-mono text-[10px] tabular-nums text-[color:var(--text-muted)] sm:h-[220px]"
            aria-hidden="true"
          >
            {geometry.gridlines.map((line) => (
              <span key={line.value} className="truncate">
                {formatValue(line.value)}
              </span>
            ))}
          </div>

          <div className="min-w-0 flex-1">
            <div
              ref={plotRef}
              role="application"
              tabIndex={0}
              aria-label={`${title} — ${series.map((s) => s.label).join(" / ")}`}
              className="relative h-[180px] w-full touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-500)] sm:h-[220px]"
              onPointerMove={handlePointer}
              onPointerDown={handlePointer}
              onPointerLeave={() => setActiveIndex(null)}
              onKeyDown={handleKey}
              onBlur={() => setActiveIndex(null)}
            >
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="dash-sales-wash" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0"
                      stopColor={series[0].color}
                      stopOpacity="0.14"
                    />
                    <stop
                      offset="1"
                      stopColor={series[0].color}
                      stopOpacity="0"
                    />
                  </linearGradient>
                </defs>
                {/* Recessive grid: solid hairlines one step off the surface. */}
                {geometry.gridlines.map((line) => (
                  <line
                    key={line.value}
                    x1="0"
                    x2="100"
                    y1={line.y}
                    y2={line.y}
                    stroke="var(--line)"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {geometry.areaPath ? (
                  <path d={geometry.areaPath} fill="url(#dash-sales-wash)" />
                ) : null}
                {series.map((s, index) => (
                  <path
                    key={s.label}
                    d={geometry.paths[index]}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>

              {/* Endpoint values, labelled directly so the latest number is
                  readable without hovering. */}
              {series.map((s) => {
                const value = s.values[lastIndex];
                if (value === undefined) return null;
                return (
                  <span
                    key={s.label}
                    className="pointer-events-none absolute -translate-y-1/2 whitespace-nowrap rounded-[6px] bg-[color:var(--card)] px-1 font-mono text-[10px] tabular-nums font-semibold text-[color:var(--text-secondary)]"
                    style={{
                      top: `${geometry.yOf(value)}%`,
                      [isRtl ? "left" : "right"]: 0,
                    }}
                  >
                    <bdi>{formatValue(value)}</bdi>
                  </span>
                );
              })}

              {/* Crosshair + markers for the hovered/focused bucket. */}
              {activeIndex !== null ? (
                <>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 w-px bg-[color:var(--border-strong)]"
                    style={{
                      left: `${geometry.xOf(activeIndex)}%`,
                    }}
                  />
                  {series.map((s) => {
                    const value = s.values[activeIndex];
                    if (value === undefined) return null;
                    return (
                      <span
                        key={s.label}
                        aria-hidden="true"
                        className="pointer-events-none absolute h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{
                          left: `${geometry.xOf(activeIndex)}%`,
                          top: `${geometry.yOf(value)}%`,
                          background: s.color,
                          // 2px surface ring keeps the marker legible where it
                          // crosses the other line.
                          boxShadow: "0 0 0 2px var(--card)",
                        }}
                      />
                    );
                  })}
                </>
              ) : null}
            </div>

            {/* Tooltip — one readout listing BOTH series at the active bucket.
                Below the plot rather than floating over it, so it can never
                cover the lines and never overflows the card on a 375px screen. */}
            <div
              role="status"
              aria-live="polite"
              className="mt-2 min-h-[42px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--soft)] px-3 py-2"
            >
              {activeIndex === null ? (
                <p className="text-[11.5px] leading-[1.5] text-[color:var(--text-muted)]">
                  {t(keys.dashboard.chartHoverHint)}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-[11.5px] font-semibold text-[color:var(--text-primary)]">
                    <bdi>{activeLabel}</bdi>
                  </span>
                  {series.map((s) => (
                    <span
                      key={s.label}
                      className="inline-flex items-center gap-1.5 text-[11.5px]"
                    >
                      <span
                        aria-hidden="true"
                        className="h-[3px] w-[12px] shrink-0 rounded-full"
                        style={{ background: s.color }}
                      />
                      {/* Value leads, series name follows — the reader already
                          knows the series and wants the number. */}
                      <span className="font-mono font-bold tabular-nums text-[color:var(--text-primary)]">
                        <bdi>{formatValue(s.values[activeIndex] ?? 0)}</bdi>
                      </span>
                      <span className="text-[color:var(--text-muted)]">
                        {s.label}
                      </span>
                    </span>
                  ))}
                  {/* Both plotted series are money; without the order count the
                      readout says how much came in but not how many orders it
                      took. This is the API's own per-bucket `orders.series`, so
                      it is a measured figure, not one inferred from the money. */}
                  <span className="text-[11.5px] font-semibold text-[color:var(--text-secondary)]">
                    <bdi>{ordersLabel(orders[activeIndex] ?? 0)}</bdi>
                  </span>
                </div>
              )}
            </div>

            {/* X axis. Rendered start→end in the reading direction: `row` under
                RTL already flows right-to-left, so the earliest tick lands at
                the right edge with no manual reversing. */}
            <div
              className="mt-1.5 flex justify-between gap-1 font-mono text-[10px] tabular-nums text-[color:var(--text-muted)]"
              aria-hidden="true"
            >
              {ticks.map((index) => (
                <span key={index} className="truncate">
                  {bucketLabels[index] ?? ""}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table view — the data is never colour-only, and every plotted value is
          reachable without hovering. */}
      <details className="mt-3 border-t border-[color:var(--line)] pt-3">
        <summary className="cursor-pointer text-[12px] font-semibold text-[color:var(--text-secondary)]">
          {t(keys.dashboard.chartTableView)}
        </summary>
        <div className="mt-2 max-h-64 overflow-auto">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="text-start text-[color:var(--text-muted)]">
                <th scope="col" className="py-1.5 pe-3 text-start font-semibold">
                  {t(keys.dashboard.chartTableBucket)}
                </th>
                {series.map((s) => (
                  <th
                    key={s.label}
                    scope="col"
                    className="py-1.5 pe-3 text-start font-semibold"
                  >
                    {s.label}
                  </th>
                ))}
                {/* The tooltip is hover-only; this column is how the per-bucket
                    order count is reached on touch and by keyboard. */}
                <th scope="col" className="py-1.5 pe-3 text-start font-semibold">
                  {t(keys.dashboard.chartTableOrders)}
                </th>
              </tr>
            </thead>
            <tbody>
              {bucketLabels.map((label, rowIndex) => (
                <tr
                  key={`${label}-${rowIndex}`}
                  className="border-t border-[color:var(--hair)]"
                >
                  <th
                    scope="row"
                    className="py-1.5 pe-3 text-start font-medium text-[color:var(--text-secondary)]"
                  >
                    <bdi>{label}</bdi>
                  </th>
                  {series.map((s) => (
                    <td
                      key={s.label}
                      className="py-1.5 pe-3 font-mono tabular-nums text-[color:var(--text-primary)]"
                    >
                      <bdi>{formatValue(s.values[rowIndex] ?? 0)}</bdi>
                    </td>
                  ))}
                  <td className="py-1.5 pe-3 font-mono tabular-nums text-[color:var(--text-secondary)]">
                    <bdi>{(orders[rowIndex] ?? 0).toLocaleString()}</bdi>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
